/**
 * ─── LAYER: lib ──────────────────────────────────────────────────────────────────
 *
 * WHAT AN OPERATOR TYPES WHEN THEY MEAN A TIME.
 *
 * The stored and transmitted form is a zero-padded 24-hour `HH:mm` — `slot.util.ts` compares
 * these as STRINGS and that soundness depends entirely on the padding, so nothing downstream
 * may ever see anything else. What somebody TYPES is a different question: `8`, `08`, `8:00`,
 * `0800`, `8 pm`, `23`, `11:30pm` are all one intention each, and refusing them is refusing
 * the way people write times.
 *
 * ⚠️ AND A HALF-TYPED ENTRY MUST NOT BECOME A REAL TIME. `8` is a complete thought; `:` is
 * not, and neither is `2` on its way to `23`. `parseTimeInput` answers `null` for anything it
 * cannot read, and the field it feeds only commits on blur — so a partial entry stays partial
 * until the operator has stopped typing it.
 */

/** `08:00`, and nothing else, is what leaves the browser. */
const CANONICAL = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const isCanonicalTime = (value: string): boolean => CANONICAL.test(value);

const pad = (value: number): string => String(value).padStart(2, '0');

export const toCanonicalTime = (hour: number, minute: number): string => `${pad(hour)}:${pad(minute)}`;

/**
 * Everything a person might type, or `null`.
 *
 * Accepted: `8` · `08` · `8:00` · `8.00` · `800` · `0800` · `23` · `2330` · `8pm` ·
 * `8 PM` · `11:30 pm` · `12am`.
 */
export const parseTimeInput = (raw: string): string | null => {
  const cleaned = raw.trim().toLowerCase().replace(/\s+/g, '');
  if (cleaned === '') return null;

  const meridiem = cleaned.endsWith('am') ? 'am' : cleaned.endsWith('pm') ? 'pm' : null;
  const body = meridiem === null ? cleaned : cleaned.slice(0, -2);
  if (body === '') return null;

  let hour: number;
  let minute: number;

  const separated = /^(\d{1,2})[:.](\d{1,2})$/.exec(body);
  if (separated) {
    hour = Number(separated[1]);
    minute = Number(separated[2]);
  } else if (/^\d+$/.test(body)) {
    /*
     * ⚠️ DIGIT COUNT IS THE WHOLE GRAMMAR, AND `800` IS THE INTERESTING ONE. Three digits
     * read as `h:mm` — `800` is 08:00, not 80 past midnight — which is how a keypad-entered
     * time is written on paper.
     */
    if (body.length <= 2) {
      hour = Number(body);
      minute = 0;
    } else if (body.length === 3) {
      hour = Number(body.slice(0, 1));
      minute = Number(body.slice(1));
    } else if (body.length === 4) {
      hour = Number(body.slice(0, 2));
      minute = Number(body.slice(2));
    } else {
      return null;
    }
  } else {
    return null;
  }

  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute > 59) return null;

  if (meridiem !== null) {
    // 12 is the odd one in both directions: 12am is midnight, 12pm is noon.
    if (hour < 1 || hour > 12) return null;
    if (meridiem === 'am' && hour === 12) hour = 0;
    if (meridiem === 'pm' && hour !== 12) hour += 12;
  }

  if (hour > 23) return null;
  return toCanonicalTime(hour, minute);
};

/**
 * Which half of the day a canonical time falls in, for the operator's eyes only.
 *
 * ⚠️ NEVER STORED AND NEVER SENT. It exists because "20:00" and "PM" are the same thought
 * to a computer and not to everyone reading a duty roster at 3 a.m.
 *
 * ⚠️ [amended P4-UX] THE MERIDIEM ALONE, NOT A SECOND CLOCK. This used to return the whole
 * 12-hour reading — `00:00` sat above `12:00 AM` and `08:00` above `8:00 AM` — which spends a
 * line repeating digits the operator is already looking at, and puts two different numbers on
 * one value in a form dense with times. The only thing a 24-hour string does not already say
 * is which half of the day it is in, so that is the only thing this says.
 */
export const toMeridiem = (canonical: string): string => {
  if (!isCanonicalTime(canonical)) return '';
  return Number(canonical.slice(0, 2)) < 12 ? 'AM' : 'PM';
};

/**
 * The dropdown's suggestions. Every half hour, filtered by what has been typed so far —
 * so typing `8` narrows to the four eights rather than making the operator scroll a day.
 */
export const timeSuggestions = (typed: string): string[] => {
  const every: string[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    every.push(toCanonicalTime(hour, 0), toCanonicalTime(hour, 30));
  }

  const parsed = parseTimeInput(typed);
  const trimmed = typed.trim();
  if (trimmed === '') return every;

  /*
   * ⚠️ A READABLE ENTRY NARROWS TO A SHORT LIST, IT DOES NOT REORDER A LONG ONE. Forty-eight
   * options with the right one at the top is still forty-eight options to look past, and it
   * is long enough that AntD virtualises it — which on a narrow screen means scrolling to
   * confirm something the operator has already typed.
   */
  if (parsed !== null) {
    /*
     * ⚠️ [amended C1f §3] A READ ENTRY OFFERS ITSELF AND NOTHING ELSE.
     *
     * This returned `[parsed, ...sameHour]`, so typing `3:35pm` put 3:00 PM and 3:30 PM
     * directly beneath it — two half hours the operator did not ask for, one arrow key from
     * the value they did, on a field that COMMITS ON BLUR. A reporting time is 16:41 as often
     * as it is 16:30.
     *
     * ⚠️ THE PARSER WAS ALWAYS RIGHT AND THE CONTROL ALWAYS TOOK FREE TEXT — [C1d] confirmed
     * `16` already reads as 4 PM. What stepped in thirty was this list, so this is the whole
     * of §3: no stepper is added, none is removed, and nothing about what the field ACCEPTS
     * changes.
     *
     * ⚠️ THE EMPTY FIELD KEEPS ALL FORTY-EIGHT, deliberately. The half hours are a
     * CONVENIENCE for somebody who has typed nothing; they are not a correction of somebody
     * who has typed a time.
     */
    return [parsed];
  }

  return every.filter((time) => time.startsWith(trimmed));
};
